var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaMagic, FaCheck, FaRedo, FaChevronLeft, FaChevronRight, FaSpinner } from 'react-icons/fa';
import { ART_SLOT_LABELS } from '@common/contracts';
import { getArtQueue, generateArtSlot, approveArtSlot, saveArtStyle, setArtSlotCandidate } from '../../api/artStudio';
import { uploadGenericImage } from '../../api/images';
import { checkGameImageSize } from '../../utils/img';
import { ABILITY_ICON } from '../../utils/gameIcons';
import { assetUrl, thumb } from '../../utils/img';
import './ArtStudio.scss';
const SIDE_LABEL = {
    trialCard: 'Card',
    trialBackground: 'Scene',
    modalCentre: 'Art',
    modalBackground: 'Backdrop',
    levelCover: 'Cover',
};
const CATEGORIES = [
    { key: 'all', label: 'All' },
    { key: 'cover', label: 'Cover' },
    { key: 'obstacle', label: 'Obstacles' },
    { key: 'panel', label: 'UX panels' },
];
function groupEntities(slots) {
    const out = [];
    const byKey = new Map();
    for (const s of slots) {
        if (s.kind === 'levelCover') {
            out.push({ key: 'cover', kind: 'cover', title: s.title, subtitle: s.subtitle, sides: [s] });
            continue;
        }
        const isObstacle = s.kind === 'trialCard' || s.kind === 'trialBackground';
        const key = isObstacle ? `obstacle:${s.coord}` : `panel:${s.slotKey.split(':').slice(1).join(':')}`;
        let e = byKey.get(key);
        if (!e) {
            e = { key, kind: isObstacle ? 'obstacle' : 'panel', title: s.title, subtitle: s.subtitle, coord: s.coord, sides: [] };
            byKey.set(key, e);
            out.push(e);
        }
        // Card/Art first, Scene/Backdrop second — stable side order.
        if (s.kind === 'trialCard' || s.kind === 'modalCentre')
            e.sides.unshift(s);
        else
            e.sides.push(s);
    }
    return out;
}
const entityStatus = (e) => {
    if (e.sides.every(s => s.status === 'approved'))
        return 'approved';
    if (e.sides.some(s => s.status !== 'empty'))
        return 'generated';
    return 'empty';
};
// The step-by-step image conveyor, one ENTITY at a time: an obstacle is its card +
// scene together, a UX panel its art + backdrop — generate either side and see the
// composed in-game screen update. The prompt editor is tabbed (Prompt | Style):
// the style is level-wide, edited once, and never eats space again.
export function ArtStudio({ levelId, levelName, onClose, onChanged }) {
    const [slots, setSlots] = useState(null);
    const [style, setStyle] = useState('');
    const [entityIndex, setEntityIndex] = useState(0);
    const [side, setSide] = useState(0);
    const [content, setContent] = useState('');
    const [promptTab, setPromptTab] = useState('prompt');
    const [category, setCategory] = useState('all');
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const stripRef = useRef(null);
    const styleSaveTimer = useRef(null);
    const entities = useMemo(() => (slots ? groupEntities(slots) : []), [slots]);
    const entity = entities[entityIndex];
    const activeSlot = entity === null || entity === void 0 ? void 0 : entity.sides[side];
    // Load the queue and resume at the first entity that isn't fully approved.
    useEffect(() => {
        let cancelled = false;
        getArtQueue(levelId)
            .then(({ style, slots }) => {
            var _a, _b, _c, _d, _e;
            if (cancelled)
                return;
            setSlots(slots);
            setStyle(style);
            const ents = groupEntities(slots);
            const first = Math.max(0, ents.findIndex(e => entityStatus(e) !== 'approved'));
            setEntityIndex(first);
            const sideIdx = (_b = (_a = ents[first]) === null || _a === void 0 ? void 0 : _a.sides.findIndex(s => s.status !== 'approved')) !== null && _b !== void 0 ? _b : 0;
            setSide((sideIdx > 0 ? 1 : 0));
            setContent((_e = (_d = (_c = ents[first]) === null || _c === void 0 ? void 0 : _c.sides[sideIdx > 0 ? 1 : 0]) === null || _d === void 0 ? void 0 : _d.prompt) !== null && _e !== void 0 ? _e : '');
        })
            .catch(() => { if (!cancelled)
            setError('Could not load the art queue.'); });
        return () => { cancelled = true; };
    }, [levelId]);
    // Entities visible under the active category.
    const visible = useMemo(() => entities.map((_, i) => i).filter(i => category === 'all' || entities[i].kind === category), [entities, category]);
    const progress = useMemo(() => {
        const acc = {
            all: { approved: 0, total: 0 }, cover: { approved: 0, total: 0 },
            obstacle: { approved: 0, total: 0 }, panel: { approved: 0, total: 0 },
        };
        for (const e of entities) {
            for (const s of e.sides) {
                for (const k of ['all', e.kind]) {
                    acc[k].total += 1;
                    if (s.status === 'approved')
                        acc[k].approved += 1;
                }
            }
        }
        return acc;
    }, [entities]);
    const gotoEntity = useCallback((i, preferSide) => {
        var _a, _b;
        const e = entities[i];
        if (!e)
            return;
        setEntityIndex(i);
        const s = preferSide !== null && preferSide !== void 0 ? preferSide : (e.sides.findIndex(x => x.status !== 'approved') === 1 ? 1 : 0);
        setSide(s);
        setContent((_b = (_a = e.sides[s]) === null || _a === void 0 ? void 0 : _a.prompt) !== null && _b !== void 0 ? _b : '');
        setPromptTab('prompt');
        setError(null);
    }, [entities]);
    const pickSide = useCallback((s) => {
        var _a, _b;
        if (!entity)
            return;
        setSide(s);
        setContent((_b = (_a = entity.sides[s]) === null || _a === void 0 ? void 0 : _a.prompt) !== null && _b !== void 0 ? _b : '');
        setPromptTab('prompt');
        setError(null);
    }, [entity]);
    // Keep the active chip in view.
    useEffect(() => {
        var _a;
        const strip = stripRef.current;
        const key = entity === null || entity === void 0 ? void 0 : entity.key;
        if (!strip || !key)
            return;
        (_a = strip.querySelector(`[data-entity="${CSS.escape(key)}"]`)) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }, [entity]);
    const pickCategory = useCallback((cat) => {
        setCategory(cat);
        const pool = entities.map((_, i) => i).filter(i => cat === 'all' || entities[i].kind === cat);
        if (!pool.length)
            return;
        const firstOpen = pool.find(i => entityStatus(entities[i]) !== 'approved');
        gotoEntity(firstOpen !== null && firstOpen !== void 0 ? firstOpen : pool[0]);
    }, [entities, gotoEntity]);
    const patchSlot = useCallback((slotKey, patch) => {
        setSlots(prev => prev ? prev.map(s => s.slotKey === slotKey ? Object.assign(Object.assign({}, s), patch) : s) : prev);
    }, []);
    const onStyleChange = useCallback((next) => {
        setStyle(next);
        if (styleSaveTimer.current)
            window.clearTimeout(styleSaveTimer.current);
        styleSaveTimer.current = window.setTimeout(() => {
            saveArtStyle(levelId, next).catch(() => { });
        }, 800);
    }, [levelId]);
    const doGenerate = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (!activeSlot || busy)
            return;
        setBusy('generate');
        setError(null);
        try {
            const res = yield generateArtSlot(levelId, activeSlot.slotKey, {
                content: content.trim() || undefined,
                style,
            });
            patchSlot(activeSlot.slotKey, { imageUrl: res.imageUrl, prompt: res.prompt, status: 'generated' });
        }
        catch (e) {
            // Same convention as AIGenerateModal: a provider failure carries a
            // user-quotable reference code, the real cause stays server-side.
            const code = (_b = (_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.code;
            setError(`Generation failed — try again, or tweak the prompt.${code ? ` (code: ${code})` : ''}`);
        }
        finally {
            setBusy(null);
        }
    }), [activeSlot, busy, levelId, content, style, patchSlot]);
    const doApprove = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!activeSlot || !entity || busy)
            return;
        setBusy('approve');
        setError(null);
        try {
            const res = yield approveArtSlot(levelId, activeSlot.slotKey);
            patchSlot(activeSlot.slotKey, { imageUrl: res.imageUrl, status: 'approved' });
            onChanged === null || onChanged === void 0 ? void 0 : onChanged();
            // The natural next stop: the entity's other side if it still needs work,
            // else the next entity (in this category) with anything unapproved.
            const otherSide = (1 - side);
            if (((_a = entity.sides[otherSide]) === null || _a === void 0 ? void 0 : _a.status) !== 'approved') {
                pickSide(otherSide);
                return;
            }
            const after = visible.find(i => i > entityIndex && entities[i].sides.some(s => s.status !== 'approved' && s.slotKey !== activeSlot.slotKey));
            const anywhere = visible.find(i => entities[i].sides.some(s => s.status !== 'approved' && s.slotKey !== activeSlot.slotKey));
            const next = after !== null && after !== void 0 ? after : anywhere;
            if (next != null)
                gotoEntity(next);
        }
        catch (_b) {
            setError('Approve failed — the image may not have generated. Try Generate first.');
        }
        finally {
            setBusy(null);
        }
    }), [activeSlot, entity, busy, levelId, patchSlot, onChanged, side, pickSide, visible, entityIndex, entities, gotoEntity]);
    // Manual path for the cover: attach an uploaded 1024×1024 file as the candidate.
    const doUpload = useCallback((file) => __awaiter(this, void 0, void 0, function* () {
        if (!activeSlot || busy)
            return;
        setError(null);
        const sizeError = yield checkGameImageSize('level-cover', file);
        if (sizeError) {
            setError(sizeError);
            return;
        }
        const up = yield uploadGenericImage(file, 'level-cover');
        if (!up.success || !up.imageUrl) {
            setError(up.error || 'Upload failed');
            return;
        }
        try {
            const res = yield setArtSlotCandidate(levelId, activeSlot.slotKey, up.imageUrl);
            patchSlot(activeSlot.slotKey, { imageUrl: res.imageUrl, status: 'generated' });
        }
        catch (_a) {
            setError('Could not attach the uploaded image.');
        }
    }), [activeSlot, busy, levelId, patchSlot]);
    const visiblePos = visible.indexOf(entityIndex);
    const gotoVisible = (step) => {
        const target = visible[visiblePos + step];
        if (target != null)
            gotoEntity(target);
    };
    if (!slots) {
        return createPortal((_jsx("div", { className: "art-studio-overlay", children: _jsxs("div", { className: "art-studio art-studio--loading", children: [error ? _jsx("p", { className: "as-error", children: error }) : _jsxs(_Fragment, { children: [_jsx(FaSpinner, { className: "as-spin" }), " Loading the art queue\u2026"] }), _jsx("button", { className: "as-close", onClick: onClose, "aria-label": "Close", children: _jsx(FaTimes, {}) })] }) })), document.body);
    }
    const counts = progress.all;
    return createPortal((_jsx("div", { className: "art-studio-overlay", children: _jsxs("div", { className: "art-studio", children: [_jsxs("header", { className: "as-head", children: [_jsxs("div", { className: "as-title", children: [_jsx(FaMagic, {}), " ", _jsx("b", { children: "Generate Art" }), " \u2014 ", levelName, _jsxs("span", { className: "as-progress-count", children: [counts.approved, " / ", counts.total, " approved"] })] }), _jsx("button", { className: "as-close", onClick: onClose, "aria-label": "Close", children: _jsx(FaTimes, {}) })] }), _jsxs("div", { className: "as-body", children: [_jsx("div", { className: "as-preview", children: _jsx(PhoneMock, { entity: entity, side: side }) }), _jsxs("div", { className: "as-right", children: [_jsx("div", { className: "as-cats", children: CATEGORIES.map(c => {
                                        const p = progress[c.key];
                                        if (c.key !== 'all' && p.total === 0)
                                            return null;
                                        return (_jsxs("button", { className: `as-cat ${category === c.key ? 'active' : ''} ${p.approved === p.total && p.total > 0 ? 'done' : ''}`, onClick: () => pickCategory(c.key), children: [c.label, " ", _jsxs("span", { className: "as-cat-count", children: [p.approved, "/", p.total] })] }, c.key));
                                    }) }), _jsx("div", { className: "as-strip", ref: stripRef, children: visible.map(i => {
                                        var _a;
                                        const e = entities[i];
                                        const cover = (_a = e.sides.find(s => s.imageUrl)) === null || _a === void 0 ? void 0 : _a.imageUrl;
                                        return (_jsxs("button", { "data-entity": e.key, className: `as-chip status-${entityStatus(e)} ${i === entityIndex ? 'current' : ''}`, onClick: () => gotoEntity(i), title: `${e.title}${e.coord ? ` (${e.coord})` : ''}`, children: [cover ? _jsx("img", { src: thumb(assetUrl(cover), 96), alt: "" }) : _jsx("span", { className: "as-chip-empty" }), _jsx("span", { className: "as-chip-sides", children: e.sides.map(s => _jsx("i", { className: `side-${s.status}` }, s.slotKey)) })] }, e.key));
                                    }) }), _jsxs("div", { className: "as-panel", children: [_jsxs("div", { className: "as-context", children: [_jsxs("div", { className: "as-kind", children: [(entity === null || entity === void 0 ? void 0 : entity.kind) === 'obstacle' ? 'Obstacle' : (entity === null || entity === void 0 ? void 0 : entity.kind) === 'panel' ? 'UX panel' : 'Level', activeSlot && _jsxs("span", { className: `as-status-pill status-${activeSlot.status}`, children: [ART_SLOT_LABELS[activeSlot.kind], " \u00B7 ", activeSlot.status] })] }), _jsxs("h3", { children: [entity === null || entity === void 0 ? void 0 : entity.title, (entity === null || entity === void 0 ? void 0 : entity.coord) ? _jsxs("span", { className: "as-coord", children: [" \u00B7 ", entity.coord] }) : null] }), (entity === null || entity === void 0 ? void 0 : entity.subtitle) && _jsx("p", { className: "as-subtitle", children: entity.subtitle })] }), _jsxs("div", { className: "as-tabs", children: [_jsx("div", { className: "as-seg", children: entity === null || entity === void 0 ? void 0 : entity.sides.map((s, i) => (_jsxs("button", { className: `as-tab ${side === i ? 'active' : ''}`, onClick: () => pickSide(i), children: [SIDE_LABEL[s.kind], _jsx("i", { className: `as-tab-dot side-${s.status}` })] }, s.slotKey))) }), _jsxs("div", { className: "as-seg", children: [_jsx("button", { className: `as-tab ${promptTab === 'prompt' ? 'active' : ''}`, onClick: () => setPromptTab('prompt'), children: "Description" }), _jsx("button", { className: `as-tab ${promptTab === 'style' ? 'active' : ''}`, onClick: () => setPromptTab('style'), children: "Style" })] })] }), promptTab === 'prompt' ? (_jsx("textarea", { className: "as-prompt", value: content, onChange: e => setContent(e.target.value), disabled: busy !== null, spellCheck: false })) : (_jsx("textarea", { className: "as-prompt", value: style, onChange: e => onStyleChange(e.target.value), disabled: busy !== null, spellCheck: false })), error && _jsx("p", { className: "as-error", children: error }), _jsxs("div", { className: "as-actions", children: [(activeSlot === null || activeSlot === void 0 ? void 0 : activeSlot.kind) === 'levelCover' && (_jsxs("label", { className: "as-btn as-upload", children: ["Upload\u2026", _jsx("input", { type: "file", accept: "image/*", hidden: true, onChange: e => { var _a; const f = (_a = e.target.files) === null || _a === void 0 ? void 0 : _a[0]; e.target.value = ''; if (f)
                                                                doUpload(f); } })] })), _jsx("button", { className: "as-btn as-generate", onClick: doGenerate, disabled: busy !== null, children: busy === 'generate' ? _jsxs(_Fragment, { children: [_jsx(FaSpinner, { className: "as-spin" }), " Generating\u2026"] })
                                                        : (activeSlot === null || activeSlot === void 0 ? void 0 : activeSlot.imageUrl) ? _jsxs(_Fragment, { children: [_jsx(FaRedo, {}), " Retry"] }) : _jsxs(_Fragment, { children: [_jsx(FaMagic, {}), " Generate"] }) }), _jsx("button", { className: "as-btn as-approve", onClick: doApprove, disabled: busy !== null || !(activeSlot === null || activeSlot === void 0 ? void 0 : activeSlot.imageUrl), children: busy === 'approve' ? _jsxs(_Fragment, { children: [_jsx(FaSpinner, { className: "as-spin" }), " Approving\u2026"] }) : _jsxs(_Fragment, { children: [_jsx(FaCheck, {}), " Approve"] }) })] }), _jsxs("div", { className: "as-nav", children: [_jsxs("button", { onClick: () => gotoVisible(-1), disabled: visiblePos <= 0, children: [_jsx(FaChevronLeft, {}), " Prev"] }), _jsxs("span", { className: "as-nav-pos", children: [visiblePos + 1, " / ", visible.length] }), _jsxs("button", { onClick: () => gotoVisible(1), disabled: visiblePos >= visible.length - 1, children: ["Next ", _jsx(FaChevronRight, {})] })] })] })] })] })] }) })), document.body);
}
// A phone-shaped mock of the REAL in-game screen for the whole ENTITY: both sides
// composed together, the active one highlighted.
//  • Obstacle → the trial screen: scene full-bleed, bottom fade, the "you VS the
//    obstacle" hero row, title + description, option rows, CTA.
//  • UX panel → the modal: backdrop full-bleed, centre art, title + text, Continue.
function PhoneMock({ entity, side }) {
    var _a, _b;
    if (!entity)
        return _jsx("div", { className: "phone-mock" });
    const url = (s) => ((s === null || s === void 0 ? void 0 : s.imageUrl) ? assetUrl(s.imageUrl) : undefined);
    if (entity.kind === 'cover') {
        const cover = url(entity.sides[0]);
        // The game's Stalkers feed: a dense 3-column grid of 1:1.12 tiles — ours
        // sits among ghost neighbours exactly where the players will find it.
        return (_jsxs("div", { className: "phone-mock", children: [_jsxs("div", { className: "phone-mock-screen pm-screen--active", children: [_jsx("div", { className: "pm-scrim" }), _jsx("div", { className: "pm-feed", children: [0, 1, 2, 3, 4, 5, 6, 7, 8].map(i => (i === 4 ? (_jsxs("div", { className: "pm-feed-tile pm-feed-tile--ours", children: [cover ? _jsx("img", { src: cover, alt: "" }) : _jsx("span", { className: "pm-empty-mini" }), _jsx("span", { className: "pm-feed-name", children: entity.subtitle || 'Level' })] }, i)) : _jsx("div", { className: "pm-feed-tile" }, i))) })] }), _jsx("p", { className: "phone-mock-caption", children: "The Stalkers feed \u2014 your level among the others" })] }));
    }
    if (entity.kind === 'obstacle') {
        const [card, scene] = entity.sides;
        const active = entity.sides[side];
        const approaches = ((_b = (_a = card === null || card === void 0 ? void 0 : card.approaches) !== null && _a !== void 0 ? _a : scene === null || scene === void 0 ? void 0 : scene.approaches) !== null && _b !== void 0 ? _b : []).slice(0, 2);
        return (_jsxs("div", { className: "phone-mock", children: [_jsxs("div", { className: `phone-mock-screen ${(active === null || active === void 0 ? void 0 : active.kind) === 'trialBackground' ? 'pm-screen--active' : ''}`, children: [url(scene)
                            ? _jsx("img", { className: "pm-bg", src: url(scene), alt: "" })
                            : _jsx("div", { className: "pm-bg pm-empty" }), _jsx("div", { className: "pm-fade" }), _jsxs("div", { className: "pm-trial", children: [_jsxs("div", { className: "pm-hero", children: [_jsx("div", { className: "pm-hero-card pm-hero-card--you", title: "Your agent \u2014 whoever the player picks", children: _jsxs("svg", { className: "pm-you", viewBox: "0 0 100 130", "aria-hidden": "true", children: [_jsx("circle", { cx: "50", cy: "36", r: "19" }), _jsx("path", { d: "M50 60 C 26 60 13 82 13 130 L 87 130 C 87 82 74 60 50 60 Z" })] }) }), _jsx("span", { className: "pm-vs", children: "VS" }), _jsx("div", { className: `pm-hero-card ${(active === null || active === void 0 ? void 0 : active.kind) === 'trialCard' ? 'pm-hero-card--active' : ''}`, children: url(card) ? _jsx("img", { src: url(card), alt: "" }) : _jsx("span", { className: "pm-enemy" }) })] }), _jsx("div", { className: "pm-title", children: entity.title }), entity.subtitle && _jsx("div", { className: "pm-desc", children: entity.subtitle }), approaches.length > 0
                                    ? approaches.map((a, i) => (_jsxs("div", { className: "pm-option pm-option--real", children: [ABILITY_ICON[a] && _jsx("img", { src: ABILITY_ICON[a], alt: "" }), _jsx("span", { children: a })] }, i)))
                                    : _jsxs(_Fragment, { children: [_jsx("div", { className: "pm-option" }), _jsx("div", { className: "pm-option" })] }), _jsx("div", { className: "pm-cta", children: "Take a chance" })] })] }), _jsx("p", { className: "phone-mock-caption", children: "The trial screen, as the player sees it" })] }));
    }
    const [art, backdrop] = entity.sides;
    const active = entity.sides[side];
    return (_jsxs("div", { className: "phone-mock", children: [_jsxs("div", { className: `phone-mock-screen ${(active === null || active === void 0 ? void 0 : active.kind) === 'modalBackground' ? 'pm-screen--active' : ''}`, children: [url(backdrop)
                        ? _jsx("img", { className: "pm-bg", src: url(backdrop), alt: "" })
                        : _jsx("div", { className: "pm-bg pm-empty" }), _jsx("div", { className: "pm-scrim" }), _jsxs("div", { className: "pm-modal", children: [_jsx("div", { className: `pm-modal-art ${(active === null || active === void 0 ? void 0 : active.kind) === 'modalCentre' ? 'pm-hero-card--active' : ''}`, children: url(art) ? _jsx("img", { src: url(art), alt: "" }) : _jsx("span", { className: "pm-empty-mini" }) }), _jsx("div", { className: "pm-title", children: entity.title }), entity.subtitle && _jsx("div", { className: "pm-desc", children: entity.subtitle }), _jsx("div", { className: "pm-cta", children: "Continue" })] })] }), _jsx("p", { className: "phone-mock-caption", children: "The UX panel, as the player sees it" })] }));
}
export default ArtStudio;
